import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime } from '../lib/dateUtils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Users, Car, LogOut, School, AlertTriangle, UserX, Plus, Building, FileText, MessageSquare, Clock, Tag, Download, RefreshCw, Calendar, Send, User, Eye, Edit, Save, Search } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import MobileActionMenu from '../components/MobileActionMenu';
import { TransportSearch } from '../components/TransportSearch';
import { LiveStats } from '../components/LiveStats';
import { AdminReviews } from '../components/reviews/AdminReviews';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [showTransportSearch, setShowTransportSearch] = useState(false);
  const [stats, setStats] = useState({});
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [allTransportOperators, setAllTransportOperators] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [pendingVehicles, setPendingVehicles] = useState([]);
  const [stickerAuditLogs, setStickerAuditLogs] = useState([]);
  const [inactiveOperators, setInactiveOperators] = useState([]);
  const [expiredPrdpDrivers, setExpiredPrdpDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showAddTransport, setShowAddTransport] = useState(false);
  const [showAllDrivers, setShowAllDrivers] = useState(false);
  const [showVehicleSticker, setShowVehicleSticker] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [showInactiveOperators, setShowInactiveOperators] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showDriverDetails, setShowDriverDetails] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showEditTransport, setShowEditTransport] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showPrdpManagement, setShowPrdpManagement] = useState(false);
  const [showPrdpChat, setShowPrdpChat] = useState(false);
  const [showUpdatePrdp, setShowUpdatePrdp] = useState(false);
  const [showPendingVehicles, setShowPendingVehicles] = useState(false);
  const [showRejectVehicle, setShowRejectVehicle] = useState(false);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showAdminsList, setShowAdminsList] = useState(false);
  const [adminsList, setAdminsList] = useState([]);
  const [showDeactivationLogs, setShowDeactivationLogs] = useState(false);
  const [deactivationLogs, setDeactivationLogs] = useState([]);
  const [showDeactivateTransport, setShowDeactivateTransport] = useState(false);
  const [transportToDeactivate, setTransportToDeactivate] = useState(null);
  const [transportDeactivationReason, setTransportDeactivationReason] = useState('');
  const [showMessageCenter, setShowMessageCenter] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState({ subject: '', message: '', recipient_role: 'transport' });
  
  // View/Edit states
  const [viewTransport, setViewTransport] = useState(null);
  const [editTransport, setEditTransport] = useState(null);
  const [viewDriver, setViewDriver] = useState(null);
  const [viewVehicle, setViewVehicle] = useState(null);
  const [viewAdmin, setViewAdmin] = useState(null);
  const [viewDeactivationLog, setViewDeactivationLog] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Selected items
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [driverToDeactivate, setDriverToDeactivate] = useState(null);
  const [accountToReactivate, setAccountToReactivate] = useState(null);
  const [selectedPrdpDriver, setSelectedPrdpDriver] = useState(null);
  const [prdpMessages, setPrdpMessages] = useState([]);
  const [selectedPendingVehicle, setSelectedPendingVehicle] = useState(null);
  const [vehicleRejectionReason, setVehicleRejectionReason] = useState('');
  
  // Form states
  const [newTransport, setNewTransport] = useState({
    email: '', full_name: '', phone: '', company_registration_number: '', sars_tax_number: ''
  });
  const [newAdmin, setNewAdmin] = useState({
    email: '', full_name: '', phone: ''
  });
  const [editTransportData, setEditTransportData] = useState({
    company_registration_number: '', sars_tax_number: ''
  });
  const [stickerNumber, setStickerNumber] = useState('');
  const [stickerReason, setStickerReason] = useState('');
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [deactivationReason, setDeactivationReason] = useState('');
  const [reactivationReason, setReactivationReason] = useState('');
  const [exportType, setExportType] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');
  const [newPrdpDate, setNewPrdpDate] = useState('');
  const [prdpMessage, setPrdpMessage] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, driversRes, allDriversRes, transportRes, vehiclesRes, auditRes, inactiveRes, prdpRes, pendingVehiclesRes] = await Promise.all([
        axios.get(`${API_URL}/stats/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/drivers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/admin/all-drivers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/admin/transport-operators`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/vehicles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/admin/sticker-audit-logs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/admin/inactive-operators`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/admin/drivers-with-expired-prdp`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/vehicles/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setPendingDrivers(driversRes.data.filter(d => d.status === 'pending'));
      // Sort drivers: active first, then archived
      const sortedDrivers = allDriversRes.data.sort((a, b) => {
        const statusOrder = { active: 0, pending: 1, deactivated: 2, archived: 3 };
        return (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4);
      });
      setAllDrivers(sortedDrivers);
      // Sort transport operators: active first
      const sortedTransport = transportRes.data.sort((a, b) => {
        const statusOrder = { active: 0, deactivated: 1, archived: 2 };
        return (statusOrder[a.status] || 3) - (statusOrder[b.status] || 3);
      });
      setAllTransportOperators(sortedTransport);
      // Filter to only active vehicles for assignment
      setAllVehicles(vehiclesRes.data.filter(v => v.status === 'active'));
      setPendingVehicles(pendingVehiclesRes.data);
      setStickerAuditLogs(auditRes.data);
      setInactiveOperators(inactiveRes.data);
      setExpiredPrdpDrivers(prdpRes.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load some dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddTransportOperator = async () => {
    try {
      const response = await axios.post(`${API_URL}/admin/transport-operators`, newTransport, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Transport operator created! Temp password: ${response.data.temp_password}`);
      setShowAddTransport(false);
      setNewTransport({ email: '', full_name: '', phone: '', company_registration_number: '', sars_tax_number: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transport operator');
    }
  };

  const handleUpdateTransportOperator = async () => {
    try {
      await axios.put(`${API_URL}/admin/transport-operators/${editTransport.user_id}`, {
        full_name: editTransport.full_name,
        phone: editTransport.phone,
        company_registration_number: editTransport.company_registration_number,
        sars_tax_number: editTransport.sars_tax_number
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transport operator details updated successfully');
      setViewTransport(null);
      setEditTransport(null);
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update transport operator');
    }
  };

  const handleUpdateSticker = async () => {
    if (!stickerNumber.trim() || !stickerReason.trim()) {
      toast.error('Please provide sticker number and reason');
      return;
    }
    try {
      await axios.put(`${API_URL}/admin/vehicles/${selectedVehicle.vehicle_id}/sticker`, 
        { sticker_number: stickerNumber, reason: stickerReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sticker number updated successfully');
      setShowVehicleSticker(false);
      setStickerNumber('');
      setStickerReason('');
      setSelectedVehicle(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sticker');
    }
  };

  const handleSendMessage = async () => {
    if (!messageSubject.trim() || !messageContent.trim()) {
      toast.error('Please provide subject and message');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/message-operator/${selectedOperator.user_id}?subject=${encodeURIComponent(messageSubject)}&message=${encodeURIComponent(messageContent)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message sent successfully');
      setShowMessageDialog(false);
      setMessageSubject('');
      setMessageContent('');
      setSelectedOperator(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const handleApproveDriver = async (driverId) => {
    try {
      await axios.put(`${API_URL}/drivers/${driverId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Driver approved successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve driver');
    }
  };

  const handleRejectDriver = async (driverId) => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await axios.put(`${API_URL}/drivers/${driverId}/reject`, { reason: rejectionReason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Driver rejected');
      setShowDriverDetails(false);
      setRejectionReason('');
      setSelectedDriver(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject driver');
    }
  };

  const handleDeactivateDriver = async () => {
    if (!deactivationReason.trim()) {
      toast.error('Please provide a reason for deactivation');
      return;
    }
    try {
      await axios.put(`${API_URL}/admin/driver/${driverToDeactivate.driver_id}/deactivate`,
        { reason: deactivationReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Driver deactivated successfully');
      setShowDeactivateDialog(false);
      setDeactivationReason('');
      setDriverToDeactivate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate driver');
    }
  };

  const handleReactivate = async () => {
    if (!reactivationReason.trim()) {
      toast.error('Please provide a reason for reactivation');
      return;
    }
    try {
      const endpoint = accountToReactivate.type === 'driver' 
        ? `${API_URL}/admin/driver/${accountToReactivate.id}/reactivate`
        : `${API_URL}/admin/transport/${accountToReactivate.id}/reactivate`;
      
      await axios.put(endpoint, { reason: reactivationReason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${accountToReactivate.type === 'driver' ? 'Driver' : 'Transport operator'} reactivated successfully`);
      setShowReactivateDialog(false);
      setReactivationReason('');
      setAccountToReactivate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate account');
    }
  };

  const handleExport = async () => {
    try {
      let endpoint = '';
      switch (exportType) {
        case 'drivers':
          endpoint = `${API_URL}/admin/export/drivers?format=${exportFormat}`;
          break;
        case 'transport_operators':
          endpoint = `${API_URL}/admin/export/transport-operators?format=${exportFormat}`;
          break;
        default:
          toast.error('Please select export type');
          return;
      }
      
      if (exportFormat === 'csv') {
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${exportType}_export.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Export downloaded successfully');
      } else {
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // For PDF, we'd need frontend PDF generation - for now just show JSON
        console.log('Export data:', response.data);
        toast.success('Export data ready (PDF generation available on frontend)');
      }
      setShowExportDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export data');
    }
  };

  const handleUpdatePrdp = async () => {
    if (!newPrdpDate) {
      toast.error('Please select a new PrDP expiry date');
      return;
    }
    try {
      await axios.put(`${API_URL}/admin/driver/${selectedPrdpDriver.driver_id}/prdp?prdp_expiry_date=${newPrdpDate}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('PrDP expiry date updated successfully');
      setShowUpdatePrdp(false);
      setNewPrdpDate('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PrDP');
    }
  };

  const handleTriggerPrdpCheck = async () => {
    try {
      await axios.post(`${API_URL}/admin/trigger-prdp-check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('PrDP notification check triggered! Emails will be sent to drivers, transport operators, and admins as needed.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger PrDP check');
    }
  };

  const fetchPrdpMessages = async (driverId) => {
    try {
      const response = await axios.get(`${API_URL}/prdp-messages/${driverId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrdpMessages(response.data);
    } catch (error) {
      console.error('Error fetching PrDP messages:', error);
    }
  };

  const handleSendPrdpMessage = async () => {
    if (!prdpMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    try {
      await axios.post(`${API_URL}/prdp-messages`, {
        driver_id: selectedPrdpDriver.driver_id,
        message: prdpMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message sent');
      setPrdpMessage('');
      fetchPrdpMessages(selectedPrdpDriver.driver_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const handleApproveVehicle = async (vehicleId) => {
    try {
      await axios.put(`${API_URL}/admin/vehicles/${vehicleId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vehicle approved successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve vehicle');
    }
  };

  const handleRejectVehicle = async () => {
    if (!vehicleRejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await axios.put(`${API_URL}/admin/vehicles/${selectedPendingVehicle.vehicle_id}/reject`, 
        { reason: vehicleRejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vehicle rejected');
      setShowRejectVehicle(false);
      setVehicleRejectionReason('');
      setSelectedPendingVehicle(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject vehicle');
    }
  };

  // Master Admin: Fetch Admins List
  const fetchAdminsList = async () => {
    try {
      const res = await axios.get(`${API_URL}/master-admin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminsList(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch admins');
    }
  };

  // Master Admin: Create Admin
  const handleCreateAdmin = async () => {
    if (!newAdmin.email || !newAdmin.full_name) {
      toast.error('Email and full name are required');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/master-admin/create-admin`, newAdmin, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Administrator created! Temporary password: ${res.data.temp_password}`);
      setShowAddAdmin(false);
      setNewAdmin({ email: '', full_name: '', phone: '' });
      fetchAdminsList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create administrator');
    }
  };


  // Fetch deactivation logs
  const fetchDeactivationLogs = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/deactivation-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeactivationLogs(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch logs');
    }
  };

  // Deactivate transport operator
  const handleDeactivateTransport = async () => {
    if (!transportDeactivationReason.trim()) {
      toast.error('Please provide a reason for deactivation');
      return;
    }
    try {
      await axios.put(`${API_URL}/admin/transport-operators/${transportToDeactivate.user_id}/deactivate`, 
        { reason: transportDeactivationReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transport operator deactivated');
      setShowDeactivateTransport(false);
      setTransportDeactivationReason('');
      setTransportToDeactivate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate');
    }
  };

  // Send broadcast message
  const handleSendBroadcast = async () => {
    if (!broadcastMessage.subject.trim() || !broadcastMessage.message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/hierarchy/messages`, {
        ...broadcastMessage,
        is_broadcast: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Broadcast sent successfully');
      setShowMessageCenter(false);
      setBroadcastMessage({ subject: '', message: '', recipient_role: 'transport' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send broadcast');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      rejected: 'bg-red-100 text-red-700',
      deactivated: 'bg-gray-100 text-gray-700',
      archived: 'bg-slate-100 text-slate-700',
      expired: 'bg-red-100 text-red-700',
      expiring_soon: 'bg-amber-100 text-amber-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0 sm:shrink-0">
            <img src="/ntwanayam-logo.jpg" alt="NtwanaYam Logo" className="w-12 h-12 rounded-xl object-cover shadow-sm hidden sm:block" data-testid="brand-logo" />
            <div className="min-w-0">
              <h1 className="text-xl sm:text-3xl font-bold text-foreground sm:whitespace-nowrap" data-testid="dashboard-title">{user?.role === 'master_admin' ? 'Master Admin Dashboard' : 'Admin Dashboard'}</h1>
              <p className="text-muted-foreground text-sm sm:text-base truncate max-w-[200px] sm:max-w-none" data-testid="user-name">Welcome, {user?.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 min-w-0 justify-end">
            <NotificationBell />
            <MobileActionMenu title="Menu">
            <Button variant="outline" onClick={() => setShowTransportSearch(true)} className="rounded-full bg-yellow-50 hover:bg-yellow-100 text-yellow-800" data-testid="search-transport-btn">
              <Search className="w-4 h-4 mr-2" /> Find Operator / Plate
            </Button>
            {/* Master Admin Only: Create Admin (Master Admins are created by IT only) */}
            {user?.role === 'master_admin' && (
              <>
                <Button variant="default" onClick={() => setShowAddAdmin(true)} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="add-admin-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Admin
                </Button>
                <Button variant="outline" onClick={() => { fetchAdminsList(); setShowAdminsList(true); }} className="rounded-full" data-testid="view-admins-btn">
                  <Users className="w-4 h-4 mr-2" /> Admins ({adminsList.length})
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowAddTransport(true)} className="rounded-full" data-testid="add-transport-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Transport
            </Button>
            <Button variant="outline" onClick={() => setShowAllDrivers(true)} className="rounded-full" data-testid="all-drivers-btn">
              <Users className="w-4 h-4 mr-2" /> All Drivers
            </Button>
            <Button variant="outline" onClick={() => setShowExportDialog(true)} className="rounded-full" data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button variant="outline" onClick={() => setShowPrdpManagement(true)} className="rounded-full" data-testid="prdp-btn">
              <Calendar className="w-4 h-4 mr-2" /> PrDP ({expiredPrdpDrivers.length})
            </Button>
            <Button variant="outline" onClick={() => setShowAuditLogs(true)} className="rounded-full" data-testid="audit-logs-btn">
              <FileText className="w-4 h-4 mr-2" /> Audit
            </Button>
            <Button variant="outline" onClick={() => { fetchDeactivationLogs(); setShowDeactivationLogs(true); }} className="rounded-full bg-red-50 hover:bg-red-100 text-red-700" data-testid="deactivation-logs-btn">
              <AlertTriangle className="w-4 h-4 mr-2" /> Archive Log
            </Button>
            <Button variant="outline" onClick={() => setShowMessageCenter(true)} className="rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700" data-testid="message-center-btn">
              <MessageSquare className="w-4 h-4 mr-2" /> Broadcast
            </Button>
            <Button variant="outline" onClick={() => setShowInactiveOperators(true)} className="rounded-full" data-testid="inactive-btn">
              <Clock className="w-4 h-4 mr-2" /> Inactive ({inactiveOperators.length})
            </Button>
            <Button variant="outline" onClick={() => setShowPendingVehicles(true)} className="rounded-full" data-testid="pending-vehicles-btn">
              <Car className="w-4 h-4 mr-2" /> Pending Vehicles ({pendingVehicles.length})
            </Button>
            <Button variant="outline" onClick={() => navigate('/schools')} className="rounded-full" data-testid="schools-btn">
              <School className="w-4 h-4 mr-2" /> Schools
            </Button>
            <Button variant="outline" onClick={() => navigate('/subscriptions')} className="rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-800" data-testid="subscriptions-btn">
              <Tag className="w-4 h-4 mr-2" /> Subscriptions
            </Button>
            <Button variant="outline" onClick={() => navigate('/profile')} className="rounded-full" data-testid="profile-btn">
              <User className="w-4 h-4 mr-2" /> Profile
            </Button>
            <Button variant="outline" onClick={logout} className="rounded-full" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
            </MobileActionMenu>
          </div>
        </div>
      </header>
      <TransportSearch open={showTransportSearch} onOpenChange={setShowTransportSearch} token={token} />

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <LiveStats token={token} />
        <AdminReviews token={token} role={user?.role} />
        {/* Master Admin Stats - Only visible to Master Admin */}
        {user?.role === 'master_admin' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
            <Card className="shadow-lg bg-indigo-50 border-indigo-200" data-testid="stat-master-admins">
              <CardHeader className="pb-2"><CardTitle className="text-lg text-indigo-700">Master Admins</CardTitle></CardHeader>
              <CardContent><p className="text-4xl font-bold text-indigo-600">{stats.total_master_admins || 0}</p></CardContent>
            </Card>
            <Card className="shadow-lg bg-violet-50 border-violet-200" data-testid="stat-admins">
              <CardHeader className="pb-2"><CardTitle className="text-lg text-violet-700">Administrators</CardTitle></CardHeader>
              <CardContent><p className="text-4xl font-bold text-violet-600">{adminsList.length || stats.total_admins || 0}</p></CardContent>
            </Card>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <Card className="shadow-lg" data-testid="stat-pending-drivers">
            <CardHeader className="pb-2"><CardTitle className="text-lg">Pending Drivers</CardTitle></CardHeader>
            <CardContent><p className="text-4xl font-bold text-amber-600">{pendingDrivers.length}</p></CardContent>
          </Card>
          <Card className="shadow-lg" data-testid="stat-total-drivers">
            <CardHeader className="pb-2"><CardTitle className="text-lg">Total Drivers</CardTitle></CardHeader>
            <CardContent><p className="text-4xl font-bold text-emerald-600">{allDrivers.length}</p></CardContent>
          </Card>
          <Card className="shadow-lg" data-testid="stat-transport-operators">
            <CardHeader className="pb-2"><CardTitle className="text-lg">Transport Ops</CardTitle></CardHeader>
            <CardContent><p className="text-4xl font-bold text-blue-600">{allTransportOperators.length}</p></CardContent>
          </Card>
          <Card className="shadow-lg" data-testid="stat-vehicles">
            <CardHeader className="pb-2"><CardTitle className="text-lg">Vehicles</CardTitle></CardHeader>
            <CardContent><p className="text-4xl font-bold text-purple-600">{allVehicles.length}</p></CardContent>
          </Card>
          <Card className="shadow-lg" data-testid="stat-prdp-issues">
            <CardHeader className="pb-2"><CardTitle className="text-lg">PrDP Issues</CardTitle></CardHeader>
            <CardContent><p className="text-4xl font-bold text-red-600">{expiredPrdpDrivers.length}</p></CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="drivers" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="drivers">Pending Drivers</TabsTrigger>
            <TabsTrigger value="transport">Transport Operators</TabsTrigger>
            <TabsTrigger value="vehicles">Vehicles & Stickers</TabsTrigger>
          </TabsList>

          {/* Pending Drivers Tab */}
          <TabsContent value="drivers">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>Pending Driver Approvals</CardTitle>
                <CardDescription>Review and approve driver applications</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingDrivers.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                    <p>No pending driver approvals</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingDrivers.map((driver) => (
                      <div key={driver.driver_id} className="flex items-center justify-between p-4 border rounded-xl hover:shadow-md transition-all" data-testid={`pending-driver-${driver.driver_id}`}>
                        <div>
                          <h3 className="font-semibold text-lg">{driver.full_name}</h3>
                          <p className="text-sm text-muted-foreground">{driver.email} | License: {driver.license_number}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewDriver(driver)} data-testid={`view-pending-driver-${driver.driver_id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" className="rounded-full" onClick={() => handleApproveDriver(driver.driver_id)} data-testid={`approve-driver-${driver.driver_id}`}>
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" className="rounded-full" onClick={() => { setSelectedDriver(driver); setShowDriverDetails(true); }} data-testid={`reject-driver-${driver.driver_id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transport Operators Tab */}
          <TabsContent value="transport">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>Transport Operators</CardTitle>
                <CardDescription>View and manage transport operators and their company details</CardDescription>
              </CardHeader>
              <CardContent>
                {allTransportOperators.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Building className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No transport operators yet</p>
                    <Button className="mt-4" onClick={() => setShowAddTransport(true)}>Add First Operator</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allTransportOperators.map((op) => (
                      <div key={op.user_id} className={`flex items-center justify-between p-4 border rounded-xl hover:shadow-md transition-all ${op.status !== 'active' ? 'opacity-60 bg-slate-50' : ''}`} data-testid={`transport-op-${op.user_id}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{op.full_name}</h3>
                            {getStatusBadge(op.status)}
                          </div>
                          <p className="text-sm text-muted-foreground">{op.email}</p>
                          <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                            <span>Phone: {op.phone || 'N/A'}</span>
                            <span>Company Reg: {op.company_registration_number || 'N/A'}</span>
                            <span>SARS Tax: {op.sars_tax_number || 'N/A'}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => { 
                            setViewTransport(op);
                            setEditTransport({...op});
                          }} data-testid={`view-transport-${op.user_id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setSelectedOperator(op); setShowMessageDialog(true); }}>
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                          {op.status === 'active' && (
                            <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:bg-red-50" onClick={() => { 
                              setTransportToDeactivate(op); 
                              setShowDeactivateTransport(true); 
                            }} data-testid={`deactivate-transport-${op.user_id}`}>
                              <UserX className="w-4 h-4" />
                            </Button>
                          )}
                          {op.status !== 'active' && (
                            <Button size="sm" variant="outline" className="rounded-full text-emerald-600" onClick={() => { 
                              setAccountToReactivate({ type: 'transport', id: op.user_id, name: op.full_name }); 
                              setShowReactivateDialog(true); 
                            }} data-testid={`reactivate-transport-${op.user_id}`}>
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vehicles Tab */}
          <TabsContent value="vehicles">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>Vehicles & Sticker Management</CardTitle>
                <CardDescription>Assign and manage vehicle sticker numbers</CardDescription>
              </CardHeader>
              <CardContent>
                {allVehicles.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Car className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No vehicles registered yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allVehicles.map((vehicle) => (
                      <div key={vehicle.vehicle_id} className="flex items-center justify-between p-4 border rounded-xl hover:shadow-md transition-all" data-testid={`vehicle-${vehicle.vehicle_id}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{vehicle.registration_number}</h3>
                            {getStatusBadge(vehicle.status)}
                          </div>
                          <p className="text-sm text-muted-foreground">{vehicle.make} {vehicle.model} ({vehicle.year}) - Capacity: {vehicle.capacity}</p>
                          {vehicle.sticker_number && (
                            <div className="flex items-center gap-2 mt-1">
                              <Tag className="w-4 h-4 text-emerald-600" />
                              <span className="text-sm font-medium text-emerald-600">Sticker: {vehicle.sticker_number}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewVehicle(vehicle)} data-testid={`view-vehicle-${vehicle.vehicle_id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" className="rounded-full" onClick={() => { setSelectedVehicle(vehicle); setStickerNumber(vehicle.sticker_number || ''); setShowVehicleSticker(true); }} data-testid={`edit-sticker-${vehicle.vehicle_id}`}>
                            <Tag className="w-4 h-4 mr-1" /> {vehicle.sticker_number ? 'Edit Sticker' : 'Assign Sticker'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Transport Operator Dialog */}
      <Dialog open={showAddTransport} onOpenChange={setShowAddTransport}>
        <DialogContent className="sm:max-w-md" data-testid="add-transport-dialog">
          <DialogHeader>
            <DialogTitle>Add Transport Operator</DialogTitle>
            <DialogDescription>Create a new transport operator account with company details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input placeholder="Transport operator name" value={newTransport.full_name} onChange={(e) => setNewTransport({...newTransport, full_name: e.target.value})} data-testid="transport-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" placeholder="email@example.com" value={newTransport.email} onChange={(e) => setNewTransport({...newTransport, email: e.target.value})} data-testid="transport-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input placeholder="078 123 4567" value={newTransport.phone} onChange={(e) => setNewTransport({...newTransport, phone: e.target.value})} data-testid="transport-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>Company Registration Number *</Label>
              <Input placeholder="e.g., 2020/123456/07" value={newTransport.company_registration_number} onChange={(e) => setNewTransport({...newTransport, company_registration_number: e.target.value})} data-testid="transport-reg-input" />
            </div>
            <div className="space-y-2">
              <Label>SARS Tax Number *</Label>
              <Input placeholder="e.g., 9123456789" value={newTransport.sars_tax_number} onChange={(e) => setNewTransport({...newTransport, sars_tax_number: e.target.value})} data-testid="transport-tax-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTransport(false)}>Cancel</Button>
            <Button onClick={handleAddTransportOperator} data-testid="create-transport-btn">Create Operator</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Edit Transport Operator Dialog */}
      <Dialog open={!!viewTransport} onOpenChange={(open) => { if (!open) { setViewTransport(null); setEditTransport(null); setIsEditing(false); }}}>
        <DialogContent className="max-w-lg" data-testid="view-transport-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEditing ? 'Edit Transport Operator' : 'Transport Operator Details'}</span>
              {!isEditing && (
                <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-transport-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewTransport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  {isEditing ? (
                    <Input value={editTransport?.full_name || ''} onChange={(e) => setEditTransport({...editTransport, full_name: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewTransport.full_name}</p>
                  )}
                </div>
                <div>
                  <Label>Email</Label>
                  <p className="text-sm font-medium mt-1">{viewTransport.email}</p>
                </div>
                <div>
                  <Label>Phone</Label>
                  {isEditing ? (
                    <Input value={editTransport?.phone || ''} onChange={(e) => setEditTransport({...editTransport, phone: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewTransport.phone || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">{getStatusBadge(viewTransport.status)}</div>
                </div>
                <div>
                  <Label>Company Registration</Label>
                  {isEditing ? (
                    <Input value={editTransport?.company_registration_number || ''} onChange={(e) => setEditTransport({...editTransport, company_registration_number: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewTransport.company_registration_number || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <Label>SARS Tax Number</Label>
                  {isEditing ? (
                    <Input value={editTransport?.sars_tax_number || ''} onChange={(e) => setEditTransport({...editTransport, sars_tax_number: e.target.value})} />
                  ) : (
                    <p className="text-sm font-medium mt-1">{viewTransport.sars_tax_number || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <Label>User ID</Label>
                  <p className="text-xs font-mono mt-1 bg-slate-100 p-1 rounded break-all">{viewTransport.user_id}</p>
                </div>
                <div>
                  <Label>Created</Label>
                  <p className="text-sm font-medium mt-1">{viewTransport.created_at ? formatDate(viewTransport.created_at) : 'N/A'}</p>
                </div>
              </div>
              {isEditing && (
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setIsEditing(false); setEditTransport({...viewTransport}); }}>Cancel</Button>
                  <Button onClick={handleUpdateTransportOperator} data-testid="save-transport-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Vehicle Dialog */}
      <Dialog open={!!viewVehicle} onOpenChange={(open) => { if (!open) setViewVehicle(null); }}>
        <DialogContent className="max-w-lg" data-testid="view-vehicle-dialog">
          <DialogHeader>
            <DialogTitle>Vehicle Details</DialogTitle>
          </DialogHeader>
          {viewVehicle && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Registration Number</Label>
                  <p className="text-sm font-medium mt-1">{viewVehicle.registration_number}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">{getStatusBadge(viewVehicle.status)}</div>
                </div>
                <div>
                  <Label>Make</Label>
                  <p className="text-sm font-medium mt-1">{viewVehicle.make}</p>
                </div>
                <div>
                  <Label>Model</Label>
                  <p className="text-sm font-medium mt-1">{viewVehicle.model}</p>
                </div>
                <div>
                  <Label>Year</Label>
                  <p className="text-sm font-medium mt-1">{viewVehicle.year}</p>
                </div>
                <div>
                  <Label>Capacity</Label>
                  <p className="text-sm font-medium mt-1">{viewVehicle.capacity} seats</p>
                </div>
                <div>
                  <Label>Sticker Number</Label>
                  <p className="text-sm font-medium mt-1 text-emerald-600">{viewVehicle.sticker_number || 'Not assigned'}</p>
                </div>
                <div>
                  <Label>Transport Operator</Label>
                  <p className="text-sm font-medium mt-1">{viewVehicle.transport_name || 'N/A'}</p>
                </div>
                <div>
                  <Label>Vehicle ID</Label>
                  <p className="text-xs font-mono mt-1 bg-slate-100 p-1 rounded break-all">{viewVehicle.vehicle_id}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Driver Dialog */}
      <Dialog open={!!viewDriver} onOpenChange={(open) => { if (!open) setViewDriver(null); }}>
        <DialogContent className="max-w-lg" data-testid="view-driver-dialog">
          <DialogHeader>
            <DialogTitle>Driver Details</DialogTitle>
          </DialogHeader>
          {viewDriver && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Full Name</Label>
                  <p className="text-sm font-medium mt-1">{viewDriver.full_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">{getStatusBadge(viewDriver.status)}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="text-sm font-medium mt-1">{viewDriver.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p className="text-sm font-medium mt-1">{viewDriver.phone || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">License Number</Label>
                  <p className="text-sm font-medium mt-1">{viewDriver.license_number || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">PrDP Expiry</Label>
                  <p className="text-sm font-medium mt-1">{viewDriver.prdp_expiry_date || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Transport Operator</Label>
                  <p className="text-sm font-medium mt-1">{viewDriver.transport_operator?.full_name || 'Unknown'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Driver ID</Label>
                  <p className="text-xs font-mono mt-1 bg-slate-100 p-1 rounded break-all">{viewDriver.driver_id}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Registered</Label>
                  <p className="text-sm font-medium mt-1">{viewDriver.created_at ? formatDateTime(viewDriver.created_at) : 'N/A'}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Admin Dialog */}
      <Dialog open={!!viewAdmin} onOpenChange={(open) => { if (!open) setViewAdmin(null); }}>
        <DialogContent className="max-w-lg" data-testid="view-admin-dialog">
          <DialogHeader>
            <DialogTitle>Administrator Details</DialogTitle>
          </DialogHeader>
          {viewAdmin && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Full Name</Label>
                  <p className="text-sm font-medium mt-1">{viewAdmin.full_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">{getStatusBadge(viewAdmin.status)}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="text-sm font-medium mt-1">{viewAdmin.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p className="text-sm font-medium mt-1">{viewAdmin.phone || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Role</Label>
                  <p className="text-sm font-medium mt-1 capitalize">{(viewAdmin.role || 'admin').replace('_', ' ')}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Created By</Label>
                  <p className="text-sm font-medium mt-1">{viewAdmin.created_by_name || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">User ID</Label>
                  <p className="text-xs font-mono mt-1 bg-slate-100 p-1 rounded break-all">{viewAdmin.user_id}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Created</Label>
                  <p className="text-sm font-medium mt-1">{viewAdmin.created_at ? formatDateTime(viewAdmin.created_at) : 'N/A'}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Deactivation Log Dialog */}
      <Dialog open={!!viewDeactivationLog} onOpenChange={(open) => { if (!open) setViewDeactivationLog(null); }}>
        <DialogContent className="max-w-lg" data-testid="view-log-dialog">
          <DialogHeader>
            <DialogTitle>Archive / Deactivation Log Details</DialogTitle>
          </DialogHeader>
          {viewDeactivationLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Target Type</Label>
                  <div className="mt-1"><Badge className="bg-red-100 text-red-700">{viewDeactivationLog.target_type}</Badge></div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Target Name</Label>
                  <p className="text-sm font-medium mt-1">{viewDeactivationLog.target_name}</p>
                </div>
                {viewDeactivationLog.target_email && (
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">Target Email</Label>
                    <p className="text-sm font-medium mt-1">{viewDeactivationLog.target_email}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Reason</Label>
                  <p className="text-sm font-medium mt-1 whitespace-pre-wrap">{viewDeactivationLog.reason}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Deactivated By</Label>
                  <p className="text-sm font-medium mt-1">{viewDeactivationLog.deactivated_by_name || 'Unknown'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">By Role</Label>
                  <p className="text-sm font-medium mt-1 capitalize">{(viewDeactivationLog.deactivated_by_role || 'N/A').replace('_', ' ')}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Date</Label>
                  <p className="text-sm font-medium mt-1">{formatDateTime(viewDeactivationLog.created_at)}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Record ID</Label>
                  <p className="text-xs font-mono mt-1 bg-slate-100 p-1 rounded break-all">{viewDeactivationLog.record_id}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* All Drivers Dialog */}
      <Dialog open={showAllDrivers} onOpenChange={setShowAllDrivers}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden" data-testid="all-drivers-dialog">
          <DialogHeader>
            <DialogTitle>All System Drivers</DialogTitle>
            <DialogDescription>View and manage all drivers (sorted: active first)</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[55vh] pr-2">
            {allDrivers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No drivers in the system yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {allDrivers.map((driver) => (
                  <div key={driver.driver_id} className={`flex items-center justify-between p-4 border rounded-xl hover:shadow-md transition-all ${driver.status !== 'active' ? 'opacity-60 bg-slate-50' : ''}`} data-testid={`all-driver-${driver.driver_id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{driver.full_name}</h3>
                        {getStatusBadge(driver.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">{driver.email} | License: {driver.license_number}</p>
                      <p className="text-xs text-muted-foreground">Transport: {driver.transport_operator?.full_name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">PrDP Expiry: {driver.prdp_expiry_date || 'N/A'}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewDriver(driver)} data-testid={`view-driver-${driver.driver_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {(driver.status === 'deactivated' || driver.status === 'archived') && (
                        <Button size="sm" variant="outline" className="rounded-full text-emerald-600" onClick={() => { 
                          setAccountToReactivate({ type: 'driver', id: driver.driver_id, name: driver.full_name }); 
                          setShowReactivateDialog(true); 
                        }} data-testid={`reactivate-${driver.driver_id}`}>
                          <RefreshCw className="w-4 h-4 mr-1" /> Reactivate
                        </Button>
                      )}
                      {driver.status === 'active' && (
                        <Button size="sm" variant="destructive" className="rounded-full" onClick={() => { setDriverToDeactivate(driver); setShowDeactivateDialog(true); }} data-testid={`deactivate-${driver.driver_id}`}>
                          <UserX className="w-4 h-4 mr-1" /> Deactivate
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAllDrivers(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle>Export Data</DialogTitle>
            <DialogDescription>Export drivers or transport operators data</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Export Type *</Label>
              <Select value={exportType} onValueChange={setExportType}>
                <SelectTrigger data-testid="export-type-select">
                  <SelectValue placeholder="Select data to export" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="drivers">All Drivers</SelectItem>
                  <SelectItem value="transport_operators">Transport Operators</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Format *</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger data-testid="export-format-select">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV (Excel Compatible)</SelectItem>
                  <SelectItem value="json">JSON (For PDF Generation)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>Cancel</Button>
            <Button onClick={handleExport} data-testid="export-download-btn">
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PrDP Management Dialog */}
      <Dialog open={showPrdpManagement} onOpenChange={setShowPrdpManagement}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden" data-testid="prdp-management-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">PrDP Expiry Management</DialogTitle>
            <DialogDescription>
              Drivers with expired or expiring PrDP within 3 months. 
              <span className="block text-xs mt-1 text-muted-foreground">
                Automated notifications run daily at 8 AM: Drivers (weekly, 3mo before), Transport (weekly, 2mo before), Admins (day 1 + every 5 days after expiry)
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="mb-4">
            <Button variant="outline" size="sm" onClick={handleTriggerPrdpCheck} data-testid="trigger-prdp-check-btn">
              <Send className="w-4 h-4 mr-2" /> Send Notifications Now
            </Button>
          </div>
          <div className="overflow-y-auto max-h-[50vh] pr-2">
            {expiredPrdpDrivers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                <p>No PrDP issues found!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {expiredPrdpDrivers.map((driver) => (
                  <div key={driver.driver_id} className={`p-4 border rounded-xl ${driver.prdp_status === 'expired' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold">{driver.full_name}</h3>
                          {getStatusBadge(driver.prdp_status)}
                        </div>
                        <p className="text-sm text-muted-foreground">{driver.email}</p>
                        <p className="text-sm font-medium text-red-600">PrDP Expiry: {driver.prdp_expiry_date}</p>
                        <p className="text-xs text-muted-foreground">Transport: {driver.transport_operator?.full_name || 'N/A'}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => { 
                          setSelectedPrdpDriver(driver); 
                          setNewPrdpDate(driver.prdp_expiry_date);
                          setShowUpdatePrdp(true); 
                        }} data-testid={`update-prdp-${driver.driver_id}`}>
                          <Calendar className="w-4 h-4 mr-1" /> Update PrDP
                        </Button>
                        <Button size="sm" className="rounded-full" onClick={() => { 
                          setSelectedPrdpDriver(driver); 
                          fetchPrdpMessages(driver.driver_id);
                          setShowPrdpChat(true); 
                        }} data-testid={`prdp-chat-${driver.driver_id}`}>
                          <MessageSquare className="w-4 h-4 mr-1" /> Communicate
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrdpManagement(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update PrDP Dialog */}
      <Dialog open={showUpdatePrdp} onOpenChange={setShowUpdatePrdp}>
        <DialogContent data-testid="update-prdp-dialog">
          <DialogHeader>
            <DialogTitle>Update PrDP Expiry Date</DialogTitle>
            <DialogDescription>Update PrDP expiry date for {selectedPrdpDriver?.full_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Current Expiry Date</Label>
              <Input value={selectedPrdpDriver?.prdp_expiry_date || ''} disabled className="bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label>New Expiry Date *</Label>
              <Input type="date" value={newPrdpDate} onChange={(e) => setNewPrdpDate(e.target.value)} data-testid="new-prdp-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdatePrdp(false)}>Cancel</Button>
            <Button onClick={handleUpdatePrdp} data-testid="save-prdp-btn">Update PrDP</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PrDP Communication Chat Dialog */}
      <Dialog open={showPrdpChat} onOpenChange={setShowPrdpChat}>
        <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="prdp-chat-dialog">
          <DialogHeader>
            <DialogTitle>PrDP Communication</DialogTitle>
            <DialogDescription>Communicate with {selectedPrdpDriver?.full_name} and their transport operator about PrDP renewal</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg max-h-60 overflow-y-auto">
              {prdpMessages.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No messages yet. Start the conversation.</p>
              ) : (
                <div className="space-y-3">
                  {prdpMessages.map((msg) => (
                    <div key={msg.message_id} className={`p-3 rounded-lg ${msg.sender_role === 'admin' ? 'bg-blue-100 ml-8' : 'bg-white mr-8 border'}`}>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span className="font-medium">{msg.sender_name} ({msg.sender_role})</span>
                        <span>{formatDateTime(msg.created_at)}</span>
                      </div>
                      <p className="text-sm">{msg.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Textarea 
                value={prdpMessage} 
                onChange={(e) => setPrdpMessage(e.target.value)} 
                placeholder="Type your message..."
                rows={2}
                data-testid="prdp-message-input"
              />
              <Button onClick={handleSendPrdpMessage} className="self-end" data-testid="send-prdp-message-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrdpChat(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate Account Dialog */}
      <Dialog open={showReactivateDialog} onOpenChange={setShowReactivateDialog}>
        <DialogContent data-testid="reactivate-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-600">Reactivate Account</DialogTitle>
            <DialogDescription>Reactivate {accountToReactivate?.name}&apos;s account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-emerald-50 p-3 rounded-lg text-emerald-800 text-sm">
              <CheckCircle className="w-4 h-4 inline-block mr-2" />
              The {accountToReactivate?.type} will be notified about this reactivation and will be able to access the system again.
            </div>
            <div className="space-y-2">
              <Label>Reason for Reactivation *</Label>
              <Textarea placeholder="Provide a reason for reactivating this account..." value={reactivationReason} onChange={(e) => setReactivationReason(e.target.value)} rows={3} data-testid="reactivation-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReactivateDialog(false); setReactivationReason(''); }}>Cancel</Button>
            <Button onClick={handleReactivate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-reactivate-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Reactivate Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vehicle Sticker Dialog */}
      <Dialog open={showVehicleSticker} onOpenChange={setShowVehicleSticker}>
        <DialogContent data-testid="sticker-dialog">
          <DialogHeader>
            <DialogTitle>{selectedVehicle?.sticker_number ? 'Update' : 'Assign'} Sticker Number</DialogTitle>
            <DialogDescription>Vehicle: {selectedVehicle?.registration_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Sticker Number (alphanumeric, max 50 chars) *</Label>
              <Input placeholder="e.g., LTA-2024-001" value={stickerNumber} onChange={(e) => setStickerNumber(e.target.value)} maxLength={50} data-testid="sticker-number-input" />
            </div>
            <div className="space-y-2">
              <Label>Reason for {selectedVehicle?.sticker_number ? 'Update' : 'Assignment'} *</Label>
              <Textarea placeholder="Provide reason for this change..." value={stickerReason} onChange={(e) => setStickerReason(e.target.value)} rows={3} data-testid="sticker-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowVehicleSticker(false); setStickerNumber(''); setStickerReason(''); }}>Cancel</Button>
            <Button onClick={handleUpdateSticker} data-testid="save-sticker-btn">Save Sticker</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sticker Audit Logs Dialog */}
      <Dialog open={showAuditLogs} onOpenChange={setShowAuditLogs}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden" data-testid="audit-logs-dialog">
          <DialogHeader>
            <DialogTitle>Sticker Change Audit Logs</DialogTitle>
            <DialogDescription>Track all sticker number changes with reasons</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[55vh] pr-2">
            {stickerAuditLogs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No audit logs yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stickerAuditLogs.map((log) => (
                  <div key={log.audit_id} className="p-4 border rounded-xl bg-slate-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">Vehicle: {log.vehicle_registration}</p>
                        <p className="text-sm">
                          {log.old_sticker_number ? `Changed from "${log.old_sticker_number}" to "${log.new_sticker_number}"` : `Assigned: "${log.new_sticker_number}"`}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Reason: {log.reason}</p>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <p>By: {log.changed_by_name}</p>
                        <p>{formatDateTime(log.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditLogs(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inactive Operators Dialog */}
      <Dialog open={showInactiveOperators} onOpenChange={setShowInactiveOperators}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden" data-testid="inactive-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-600">Inactive Transport Operators (14+ days)</DialogTitle>
            <DialogDescription>Operators who haven&apos;t started or ended trips in the last 14 days</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[55vh] pr-2">
            {inactiveOperators.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                <p>All operators are active!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {inactiveOperators.map((op) => (
                  <div key={op.user_id} className="flex items-center justify-between p-4 border border-amber-200 rounded-xl bg-amber-50">
                    <div>
                      <h3 className="font-semibold">{op.full_name}</h3>
                      <p className="text-sm text-muted-foreground">{op.email}</p>
                      <p className="text-xs text-amber-600">Last activity: {op.last_trip_activity ? formatDate(op.last_trip_activity) : 'Never'}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setViewTransport(op); setEditTransport({...op}); setShowInactiveOperators(false); }} data-testid={`view-inactive-${op.user_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" className="rounded-full" onClick={() => { setSelectedOperator(op); setShowMessageDialog(true); setShowInactiveOperators(false); }}>
                        <MessageSquare className="w-4 h-4 mr-1" /> Send Message
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInactiveOperators(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent data-testid="message-dialog">
          <DialogHeader>
            <DialogTitle>Send Message to {selectedOperator?.full_name}</DialogTitle>
            <DialogDescription>Send a message or inquiry to this transport operator</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input placeholder="Message subject" value={messageSubject} onChange={(e) => setMessageSubject(e.target.value)} data-testid="message-subject-input" />
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea placeholder="Write your message..." value={messageContent} onChange={(e) => setMessageContent(e.target.value)} rows={5} data-testid="message-content-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowMessageDialog(false); setMessageSubject(''); setMessageContent(''); }}>Cancel</Button>
            <Button onClick={handleSendMessage} data-testid="send-message-btn">Send Message</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Driver Dialog */}
      <Dialog open={showDriverDetails} onOpenChange={setShowDriverDetails}>
        <DialogContent data-testid="reject-driver-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Reject Driver</DialogTitle>
            <DialogDescription>Provide a reason for rejecting {selectedDriver?.full_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <Textarea placeholder="Reason for rejection..." value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} rows={3} data-testid="rejection-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDriverDetails(false); setRejectionReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleRejectDriver(selectedDriver?.driver_id)} data-testid="confirm-reject-btn">Reject Driver</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Driver Dialog */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent data-testid="deactivate-driver-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Deactivate Driver</DialogTitle>
            <DialogDescription>Deactivating {driverToDeactivate?.full_name} will prevent them from accessing the system</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
              <AlertTriangle className="w-4 h-4 inline-block mr-2" />
              The driver will be notified about this deactivation.
            </div>
            <div className="space-y-2">
              <Label>Deactivation Reason *</Label>
              <Textarea placeholder="Reason for deactivation..." value={deactivationReason} onChange={(e) => setDeactivationReason(e.target.value)} rows={3} data-testid="deactivation-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeactivateDialog(false); setDeactivationReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeactivateDriver} data-testid="confirm-deactivate-btn">
              <UserX className="w-4 h-4 mr-2" /> Confirm Deactivation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Vehicles Dialog */}
      <Dialog open={showPendingVehicles} onOpenChange={setShowPendingVehicles}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden" data-testid="pending-vehicles-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-600">Pending Vehicle Approvals</DialogTitle>
            <DialogDescription>Review and approve or reject vehicle registrations from transport operators</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[55vh] pr-2">
            {pendingVehicles.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                <p>No pending vehicle approvals</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingVehicles.map((vehicle) => (
                  <div key={vehicle.vehicle_id} className="p-4 border border-amber-200 rounded-xl bg-amber-50" data-testid={`pending-vehicle-${vehicle.vehicle_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-lg">{vehicle.registration_number}</h3>
                          {getStatusBadge(vehicle.status)}
                        </div>
                        <p className="text-sm text-muted-foreground">{vehicle.make} {vehicle.model} ({vehicle.year}) - Capacity: {vehicle.capacity}</p>
                        <p className="text-xs text-muted-foreground mt-1">Operator: {vehicle.transport_operator_name || 'Unknown'}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewVehicle(vehicle)} data-testid={`view-pending-vehicle-${vehicle.vehicle_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveVehicle(vehicle.vehicle_id)} data-testid={`approve-vehicle-${vehicle.vehicle_id}`}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive" className="rounded-full" onClick={() => { setSelectedPendingVehicle(vehicle); setShowRejectVehicle(true); }} data-testid={`reject-vehicle-${vehicle.vehicle_id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPendingVehicles(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Vehicle Dialog */}
      <Dialog open={showRejectVehicle} onOpenChange={setShowRejectVehicle}>
        <DialogContent data-testid="reject-vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Reject Vehicle</DialogTitle>
            <DialogDescription>Provide a reason for rejecting vehicle {selectedPendingVehicle?.registration_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <Textarea placeholder="Reason for rejection..." value={vehicleRejectionReason} onChange={(e) => setVehicleRejectionReason(e.target.value)} rows={3} data-testid="vehicle-rejection-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectVehicle(false); setVehicleRejectionReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectVehicle} data-testid="confirm-reject-vehicle-btn">Reject Vehicle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Master Admin: Add Admin Dialog */}
      <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
        <DialogContent data-testid="add-admin-dialog">
          <DialogHeader>
            <DialogTitle className="text-indigo-600">Create Administrator</DialogTitle>
            <DialogDescription>Add a new administrator to the system. They will receive a temporary password via email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input placeholder="Full name" value={newAdmin.full_name} onChange={(e) => setNewAdmin({...newAdmin, full_name: e.target.value})} data-testid="new-admin-name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" placeholder="Email address" value={newAdmin.email} onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})} data-testid="new-admin-email" />
            </div>
            <div className="space-y-2">
              <Label>Phone (optional)</Label>
              <Input placeholder="+27..." value={newAdmin.phone} onChange={(e) => setNewAdmin({...newAdmin, phone: e.target.value})} data-testid="new-admin-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddAdmin(false); setNewAdmin({ email: '', full_name: '', phone: '' }); }}>Cancel</Button>
            <Button onClick={handleCreateAdmin} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-create-admin-btn">Create Administrator</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Master Admin: View Admins List Dialog */}
      <Dialog open={showAdminsList} onOpenChange={setShowAdminsList}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden" data-testid="admins-list-dialog">
          <DialogHeader>
            <DialogTitle className="text-indigo-600">Administrators</DialogTitle>
            <DialogDescription>All administrators in the system</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[55vh] pr-2">
            {adminsList.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p>No administrators found</p>
                <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700" onClick={() => { setShowAdminsList(false); setShowAddAdmin(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Create First Admin
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {adminsList.map((admin) => (
                  <div key={admin.user_id} className="p-4 border rounded-xl hover:bg-slate-50" data-testid={`admin-${admin.user_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-lg">{admin.full_name}</h3>
                          {getStatusBadge(admin.status)}
                        </div>
                        <p className="text-sm text-muted-foreground">{admin.email}</p>
                        {admin.phone && <p className="text-xs text-muted-foreground">{admin.phone}</p>}
                        {admin.created_by_name && (
                          <p className="text-xs text-muted-foreground mt-1">Created by: {admin.created_by_name}</p>
                        )}
                      </div>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewAdmin(admin)} data-testid={`view-admin-${admin.user_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminsList(false)}>Close</Button>
            <Button onClick={() => { setShowAdminsList(false); setShowAddAdmin(true); }} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" /> Add Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivation Audit Logs Dialog */}
      <Dialog open={showDeactivationLogs} onOpenChange={setShowDeactivationLogs}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden" data-testid="deactivation-logs-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Archive / Deactivation Logs</DialogTitle>
            <DialogDescription>History of all accounts and records that have been archived or deactivated</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[55vh] pr-2">
            {deactivationLogs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p>No archive logs found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {deactivationLogs.map((log) => (
                  <div key={log.record_id} className="p-4 border rounded-xl bg-slate-50" data-testid={`log-${log.record_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-red-100 text-red-700">{log.target_type}</Badge>
                          <h3 className="font-semibold">{log.target_name}</h3>
                        </div>
                        {log.target_email && <p className="text-sm text-muted-foreground">{log.target_email}</p>}
                        <p className="text-sm mt-2"><strong>Reason:</strong> {log.reason}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          By: {log.deactivated_by_name || 'Unknown'} ({log.deactivated_by_role || 'N/A'})
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(log.created_at)}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewDeactivationLog(log)} data-testid={`view-log-${log.record_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivationLogs(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Deactivate Transport Operator Dialog */}
      <Dialog open={showDeactivateTransport} onOpenChange={setShowDeactivateTransport}>
        <DialogContent data-testid="deactivate-transport-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Deactivate Transport Operator</DialogTitle>
            <DialogDescription>Deactivating {transportToDeactivate?.full_name} will prevent them from accessing the system</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
              <AlertTriangle className="w-4 h-4 inline-block mr-2" />
              This will also affect all their drivers and service.
            </div>
            <div className="space-y-2">
              <Label>Deactivation Reason *</Label>
              <Textarea placeholder="Reason for deactivation..." value={transportDeactivationReason} onChange={(e) => setTransportDeactivationReason(e.target.value)} rows={3} data-testid="transport-deactivation-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeactivateTransport(false); setTransportDeactivationReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeactivateTransport} data-testid="confirm-deactivate-transport-btn">
              <UserX className="w-4 h-4 mr-2" /> Confirm Deactivation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Center / Broadcast Dialog */}
      <Dialog open={showMessageCenter} onOpenChange={setShowMessageCenter}>
        <DialogContent data-testid="message-center-dialog">
          <DialogHeader>
            <DialogTitle className="text-blue-600">Broadcast Message</DialogTitle>
            <DialogDescription>Send a message to all users of a specific role</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Send to Role</Label>
              <Select value={broadcastMessage.recipient_role} onValueChange={(v) => setBroadcastMessage({...broadcastMessage, recipient_role: v})}>
                <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transport">All Transport Operators</SelectItem>
                  <SelectItem value="driver">All Drivers</SelectItem>
                  <SelectItem value="parent">All Parents</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input placeholder="Message subject" value={broadcastMessage.subject} onChange={(e) => setBroadcastMessage({...broadcastMessage, subject: e.target.value})} data-testid="broadcast-subject" />
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea placeholder="Your message..." value={broadcastMessage.message} onChange={(e) => setBroadcastMessage({...broadcastMessage, message: e.target.value})} rows={4} data-testid="broadcast-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageCenter(false)}>Cancel</Button>
            <Button onClick={handleSendBroadcast} className="bg-blue-600 hover:bg-blue-700" data-testid="send-broadcast-btn">
              <Send className="w-4 h-4 mr-2" /> Send Broadcast
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
